import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Calendar, ArrowRight } from "lucide-react";
import { Reveal, Eyebrow } from "./shared";
import { resolveUrl } from "@/lib/api";

export const News = ({ news = [] }) => {
  const [selectedNews, setSelectedNews] = useState(null);

  if (!news || news.length === 0) return null;

  return (
    <section id="news" data-testid="news-section" className="relative py-24 md:py-32">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal>
          <Eyebrow testid="news-eyebrow">Berita & Pengumuman</Eyebrow>
          <h2 className="mt-6 font-heading font-bold text-3xl md:text-5xl tracking-tight leading-tight">
            Update terbaru dan <span className="text-cyan">informasi terkini</span>.
          </h2>
        </Reveal>

        {/* Tampilan Ringkas (Judul & Hook/Ringkasan yang SEO-friendly) */}
        <div className="mt-14 grid md:grid-cols-3 gap-8">
          {news.map((item, i) => (
            <motion.article 
              key={item.id || i}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.6, delay: i * 0.1 }}
              onClick={() => setSelectedNews(item)}
              data-testid={`news-card-${i}`}
              className="group cursor-pointer rounded-3xl border border-white/10 bg-navy-card/50 overflow-hidden hover:border-gold/40 hover:-translate-y-2 transition-all duration-300 flex flex-col justify-between"
            >
              <div>
                <div className="h-52 overflow-hidden relative bg-navy-soft">
                  {item.image_url && (
                    <img src={resolveUrl(item.image_url)} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-transparent" />
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 text-xs font-semibold text-gold mb-3">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>{item.date}</span>
                  </div>
                  {/* Judul Berita yang SEO-friendly */}
                  <h3 className="font-heading font-bold text-xl mb-3 leading-snug text-white group-hover:text-cyan transition-colors">{item.title}</h3>
                  {/* Hook / Ringkasan Singkat untuk memancing pembaca */}
                  <p className="text-sm text-slate-400 line-clamp-3 leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </div>

              <div className="px-6 pb-6 pt-2 flex items-center gap-2 text-sm font-semibold text-cyan group-hover:gap-3 transition-all">
                <span>Baca selengkapnya</span>
                <ArrowRight className="w-4 h-4" />
              </div>
            </motion.article>
          ))}
        </div>
      </div>

      {/* POPUP / MODAL DETAIL BERITA LENGKAP */}
      <AnimatePresence>
        {selectedNews && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSelectedNews(null)}
            className="fixed inset-0 z-[100] bg-navy/90 backdrop-blur-md grid place-items-center p-4 overflow-y-auto"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-3xl rounded-3xl border border-white/15 bg-navy-card p-6 md:p-10 shadow-2xl overflow-hidden my-8 max-h-[90vh] flex flex-col"
            >
              <button
                onClick={() => setSelectedNews(null)}
                className="absolute top-4 right-4 z-10 grid place-items-center w-10 h-10 rounded-full border border-white/10 bg-navy/80 text-slate-300 hover:bg-white/10 hover:text-white transition shadow-lg"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="overflow-y-auto pr-2 space-y-6">
                {selectedNews.image_url && (
                  <div className="w-full h-64 md:h-80 rounded-2xl overflow-hidden border border-white/10 shrink-0">
                    <img src={resolveUrl(selectedNews.image_url)} alt={selectedNews.title} className="w-full h-full object-cover" />
                  </div>
                )}

                <div>
                  <div className="flex items-center gap-2 text-xs font-semibold text-gold mb-2">
                    <Calendar className="w-3.5 h-3.5" />
                    <span>{selectedNews.date}</span>
                  </div>
                  <h2 className="font-heading font-bold text-2xl md:text-4xl text-white leading-tight">
                    {selectedNews.title}
                  </h2>
                </div>

                <div className="pt-6 border-t border-white/10">
                  {/* Isi Berita Lengkap */}
                  <div className="text-sm md:text-base text-slate-300 leading-relaxed whitespace-pre-wrap space-y-4">
                    {selectedNews.desc}
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};